// Level calculations using geometric progression: each level requires
// progressively more points (points_for_level_n = base * multiplier^(n-1)).
//
// The values below are the HARDCODED DEFAULTS — the offline/fallback truth.
// On boot, xpConfigStore fetches the console-managed config tables
// (xp_config / xp_actions / xp_rates, migration 0191) and hydrateXpConfig()
// overwrites these in place, so an admin edit in the console tunes the whole
// app without a deploy. Unreachable config (offline, pre-migration) = these
// defaults = today's behavior exactly.
//
// ⚠️ Curve values (XP_CURVE) are RETROACTIVE — levels are derived from XP
// totals at render time, so changing them re-levels users instantly. Action
// values (POINTS) only affect future awards (records store points at award
// time).
export const XP_CURVE = {
  domainBasePoints: 100,     // Points needed for domain level 0→1
  userBasePoints: 700,       // Points needed for user level 0→1 (7x higher than domain)
  levelMultiplier: 1.17,     // Each level needs 17% more points than previous
  levelsPerTier: 100,        // Levels per tier cycle
  pointsScaling: 1.14,       // Action-points growth per user level (intentionally slower than levelMultiplier)
  domainStreakDamping: 0.5,  // Domain streak bonus = 1 + (maxMultiplier − 1) × damping
};

// Streak ladder — shared by habit completions and breathwork streaks.
// Kept sorted by threshold ascending; lookup walks from the top.
export const STREAK_LADDER: Array<{ threshold: number; multiplier: number }> = [
  { threshold: 0, multiplier: 1.0 },
  { threshold: 3, multiplier: 1.1 },
  { threshold: 7, multiplier: 1.2 },
  { threshold: 14, multiplier: 1.4 },
  { threshold: 30, multiplier: 1.6 },
  { threshold: 60, multiplier: 2.0 },
  { threshold: 90, multiplier: 3.0 },
];

// Program difficulty multipliers (consumed by utils/programXP.ts).
export const PROGRAM_DIFFICULTY_MULTIPLIERS: Record<string, number> = {
  beginner: 1.0,
  intermediate: 1.2,
  advanced: 1.5,
  expert: 2.0,
};

// Tier system definitions — hydratable from xp_titles(kind='tier'),
// kept sorted by minLevel ascending (indexed math below relies on it).
export const TIERS: Array<{ name: string; minLevel: number; color: string }> = [
  { name: 'Bronze', minLevel: 0, color: '#cd7f32' },
  { name: 'Silver', minLevel: 100, color: '#c0c0c0' },
  { name: 'Gold', minLevel: 200, color: '#ffd700' },
  { name: 'Platinum', minLevel: 300, color: '#e5e4e2' },
  { name: 'Diamond', minLevel: 400, color: '#b9f2ff' },
  { name: 'Onyx', minLevel: 500, color: '#353839' },
  { name: 'Obsidian', minLevel: 600, color: '#0f0f0f' }
];

// Points constants — mutable so hydrateXpConfig() can overwrite from the
// console-managed xp_actions table (defaults = the values below).
export const POINTS: Record<string, number> = {
  TASK: 10,
  SUBTASK: 5,
  HABIT: 15,
  EVENT: 20,
  PROJECT_TASK: 10,
  PROJECT_SUBTASK: 5,
  PROJECT_MILESTONE: 100,
  PROJECT_COMPLETION: 50,
  GOAL_COMPLETION: 100,
  ROUTINE_MINUTE: 5,
  QUEST_GOAL_BASE: 50,
  QUEST_BASE_BONUS: 100,
  QUEST_PER_GOAL_BONUS: 50,
  SKILL_REQUIREMENT_BASE: 25,
  SKILL_OUTPUT_BONUS: 15,
  SKILL_FOUNDATION_COMPLETION: 50,
  SKILL_DEVELOPING_COMPLETION: 60,
  SKILL_INTERMEDIATE_COMPLETION: 75,
  SKILL_PROFICIENT_COMPLETION: 90,
  SKILL_ADVANCED_COMPLETION: 100,
  SKILL_EXPERT_COMPLETION: 115,
  SKILL_MASTERY_COMPLETION: 125,
  SKILL_CAPSTONE_BONUS: 150, // extra one-time bonus when a converging-shape capstone skill completes
  ABILITY_LEVEL_UP: 100,
  BREATHWORK_PER_SECOND: 1,
  // 1.2 = the app's actual historical behavior (breathworkStore had its own
  // local constant until Phase C; the old 1.14 here was never applied).
  BREATHWORK_SCALING: 1.2,
  PROGRAM_MODULE_BASE: 50,
  PROGRAM_COMPLETION_BONUS: 175,
  PROGRAM_STREAK_BONUS: 10,
  PROGRAM_DURATION_BONUS_MULTIPLIER: 1.5,
  // Tracker daily caps (render-time — retroactive on edit)
  TRACKER_HYDRATION_DAILY: 50,
  TRACKER_NUTRITION_DAILY: 50,
  TRACKER_SUPPLEMENTS_DAILY: 50,
  TRACKER_SLEEP_DAILY: 50,
  TRACKER_RECOVERY_DAILY: 50,
  TRACKER_HEALTH_DAILY: 50,
  TRACKER_GROOMING_DAILY: 50,
  // ⚠️ The five TRACKER_MIND_* rates (clarity check + four subsystem acts) were
  // retired with the Mind subsystems on 2026-09-30 (scope 006, "start fresh").
  // Mind's care practices (scope 006 PR B, `utils/carePractice.sumCareXP`): per
  // practice per day, capped per day so adding practices cannot farm it.
  // Seeded as console-tunable xp_actions rows by migration 0275.
  TRACKER_CARE_PRACTICE: 25,
  TRACKER_CARE_DAILY_CAP: 50,
  // Ecological derived XP (render-time — retroactive on edit)
  ECO_TRANSACTION: 5,
  ECO_TRANSACTION_CATEGORIZED: 2,
  ECO_BUDGET_CREATED: 20,
  ECO_BUDGET_MET: 50,
  ECO_POSSESSION_ASSET: 10,
  ECO_POSSESSION_MAINTENANCE: 15,
  ECO_RESOURCE_ITEM: 10,
  ECO_RESOURCE_TASK: 15,
  ECO_TIME_DAY_BASE: 5,
  ECO_TIME_DOMAIN_COVERAGE: 4,
  // Intellectual learning (write-time — future sessions only)
  LEARN_MINUTE: 2,
  LEARN_RESOURCE_BOOK: 100,
  LEARN_RESOURCE_COURSE: 200,
  LEARN_RESOURCE_ARTICLE: 25,
  LEARN_RESOURCE_VIDEO: 50,
  LEARN_RESOURCE_PODCAST: 40,
  LEARN_RESOURCE_NOTE: 15
};

// Tracker formula params (hydrated from xp_rates kind='tracker_param').
export const TRACKER_PARAMS = {
  nutritionMealGoal: 3,   // meals logged for a full nutrition day
  healthVitalsRatio: 0.5, // vitals-only day = this × the full check-in XP
};

// ============================================================================
// Config hydration — called by xpConfigStore after fetching migration 0191's
// tables. Mutates the exported objects in place so every consumer (72 files
// import from here) picks the values up without refactoring. Rows that are
// missing or inactive keep their hardcoded default.
// ============================================================================
export interface XpConfigRow {
  user_base_points: number;
  domain_base_points: number;
  level_multiplier: number;
  points_scaling: number;
  levels_per_tier: number;
  domain_streak_damping: number;
}
export interface XpActionRow { key: string; base_xp: number; active: boolean; category: string }
export interface XpRateRow { kind: string; key: string; threshold: number | null; multiplier: number; active: boolean }
export interface XpTitleRow { kind: string; key: string; name: string; min_level: number; min_xp: number | null; color: string | null; description: string; domain_id?: string | null; meaning_title?: string | null; meaning_body?: string | null }

/**
 * One rung of a title ladder.
 *
 * ⚠️ `meaningTitle`/`meaningBody` are OPTIONAL and often absent: they arrive
 * from `xp_titles` (migration 0241) and are blank until someone fills them in
 * from the console. An empty one means "use the app's built-in, position-keyed
 * copy" (`constants/titleFlavour.ts`) — never render it as empty text.
 */
export interface RankRung {
  name: string;
  minLevel: number;
  meaningTitle?: string;
  meaningBody?: string;
}

export function hydrateXpConfig(
  config: XpConfigRow | null,
  actions: XpActionRow[],
  rates: XpRateRow[],
  titles: XpTitleRow[] = [],
): void {
  if (config) {
    if (Number.isFinite(config.user_base_points) && config.user_base_points > 0) XP_CURVE.userBasePoints = config.user_base_points;
    if (Number.isFinite(config.domain_base_points) && config.domain_base_points > 0) XP_CURVE.domainBasePoints = config.domain_base_points;
    if (Number.isFinite(config.level_multiplier) && config.level_multiplier > 1) XP_CURVE.levelMultiplier = config.level_multiplier;
    if (Number.isFinite(config.points_scaling) && config.points_scaling >= 1) XP_CURVE.pointsScaling = config.points_scaling;
    if (Number.isFinite(config.levels_per_tier) && config.levels_per_tier > 0) XP_CURVE.levelsPerTier = config.levels_per_tier;
    if (Number.isFinite(config.domain_streak_damping) && config.domain_streak_damping >= 0) XP_CURVE.domainStreakDamping = config.domain_streak_damping;
  }

  for (const a of actions) {
    // Social rows are server-enforced (award_social_xp ledger) — skip here.
    if (!a.active || a.category === 'social') continue;
    const pointsKey = a.key.toUpperCase();
    if (pointsKey in POINTS && Number.isFinite(a.base_xp) && a.base_xp >= 0) {
      POINTS[pointsKey] = a.base_xp;
    }
  }

  const ladder = rates
    .filter((r) => r.kind === 'streak_ladder' && r.active && Number.isFinite(r.multiplier))
    .map((r) => ({ threshold: Number(r.threshold ?? 0), multiplier: r.multiplier }))
    .sort((x, y) => x.threshold - y.threshold);
  if (ladder.length > 0) {
    STREAK_LADDER.length = 0;
    STREAK_LADDER.push(...ladder);
  }

  for (const r of rates) {
    if (r.kind !== 'tracker_param' || !r.active || !Number.isFinite(r.multiplier)) continue;
    if (r.key === 'nutrition_meal_goal' && r.multiplier > 0) TRACKER_PARAMS.nutritionMealGoal = r.multiplier;
    if (r.key === 'health_vitals_ratio' && r.multiplier >= 0) TRACKER_PARAMS.healthVitalsRatio = r.multiplier;
  }

  for (const r of rates) {
    if (r.kind === 'program_difficulty' && r.active && Number.isFinite(r.multiplier) && r.multiplier > 0) {
      PROGRAM_DIFFICULTY_MULTIPLIERS[r.key] = r.multiplier;
    }
  }

  // Title ladders. USER_RANKS/DOMAIN_RANKS lookup expects DESCENDING minLevel;
  // TIERS expects ASCENDING (index math over levelsPerTier steps).
  const rankRows = (rows: XpTitleRow[]): RankRung[] =>
    rows
      .filter((t) => Number.isFinite(t.min_level))
      .sort((a, b) => b.min_level - a.min_level)
      // ⚠️ `|| undefined` collapses BOTH null and '' — the column is
      // `not null default ''`, so an unfilled row arrives as an empty string,
      // and passing that through would print a blank meaning on the card
      // instead of falling back to the built-in copy.
      .map((t) => ({
        name: t.name,
        minLevel: t.min_level,
        meaningTitle: t.meaning_title || undefined,
        meaningBody: t.meaning_body || undefined,
      }));
  const userRanks = rankRows(titles.filter((t) => t.kind === 'user_rank'));
  if (userRanks.length > 0) {
    USER_RANKS.length = 0;
    USER_RANKS.push(...userRanks);
  }
  // domain_rank: rows without a domain_id form the generic ladder; rows with
  // one form that domain's own ladder (0194).
  const domainRankRows = titles.filter((t) => t.kind === 'domain_rank');
  const genericDomainRanks = rankRows(domainRankRows.filter((t) => !t.domain_id));
  if (genericDomainRanks.length > 0) {
    DOMAIN_RANKS.length = 0;
    DOMAIN_RANKS.push(...genericDomainRanks);
  }
  for (const key of Object.keys(DOMAIN_RANKS_BY_DOMAIN)) delete DOMAIN_RANKS_BY_DOMAIN[key];
  for (const row of domainRankRows) {
    if (!row.domain_id) continue;
    (DOMAIN_RANKS_BY_DOMAIN[row.domain_id] ??= []);
  }
  for (const domainId of Object.keys(DOMAIN_RANKS_BY_DOMAIN)) {
    DOMAIN_RANKS_BY_DOMAIN[domainId] = rankRows(domainRankRows.filter((t) => t.domain_id === domainId));
  }
  const tiers = titles
    .filter((t) => t.kind === 'tier' && Number.isFinite(t.min_level))
    .sort((a, b) => a.min_level - b.min_level)
    .map((t) => ({ name: t.name, minLevel: t.min_level, color: t.color || '#cd7f32' }));
  if (tiers.length > 0) {
    TIERS.length = 0;
    TIERS.push(...tiers);
  }
}

// User Ranks — hydratable from xp_titles(kind='user_rank'); kept sorted by
// minLevel DESCENDING (getUserRank finds the first match from the top).
export const USER_RANKS: RankRung[] = [
  { name: 'Ethereal', minLevel: 100 },
  { name: 'Ascendant', minLevel: 90 },
  { name: 'Legendary', minLevel: 80 },
  { name: 'Champion', minLevel: 70 },
  { name: 'Master', minLevel: 60 },
  { name: 'Expert', minLevel: 50 },
  { name: 'Proficient', minLevel: 40 },
  { name: 'Journeyman', minLevel: 30 },
  { name: 'Apprentice', minLevel: 20 },
  { name: 'Novice', minLevel: 10 },
  { name: 'Rookie', minLevel: 1 },
  { name: 'Beginner', minLevel: 0 }
];

// Domain Ranks — the GENERIC ladder, used for any domain without its own set.
// Same hydration + descending-order contract as USER_RANKS. Per-domain
// ladders (xp_titles rows with a domain_id, migration 0194) hydrate into
// DOMAIN_RANKS_BY_DOMAIN below; offline/pre-migration every domain falls
// back to this generic set (the pre-0194 behavior).
export const DOMAIN_RANKS: RankRung[] = [
  { name: 'Transcendent', minLevel: 100 },
  { name: 'Sovereign', minLevel: 90 },
  { name: 'Mythical', minLevel: 80 },
  { name: 'Elite', minLevel: 70 },
  { name: 'Veteran', minLevel: 60 },
  { name: 'Adept', minLevel: 50 },
  { name: 'Skilled', minLevel: 40 },
  { name: 'Practiced', minLevel: 30 },
  { name: 'Capable', minLevel: 20 },
  { name: 'Initiated', minLevel: 10 },
  { name: 'Aspiring', minLevel: 1 },
  { name: 'Novice', minLevel: 0 }
];

// Per-domain rank ladders (hydrated from xp_titles rows with a domain_id;
// each sorted by minLevel DESCENDING like DOMAIN_RANKS).
export const DOMAIN_RANKS_BY_DOMAIN: Record<string, RankRung[]> = {};

// Get rank based on total level
export function getUserRank(totalLevel: number) {
  return USER_RANKS.find(rank => totalLevel >= rank.minLevel) || USER_RANKS[USER_RANKS.length - 1];
}

// The full rank ladder for a domain: that domain's own set when one exists
// (per-domain, migration 0194), else the generic DOMAIN_RANKS fallback.
// Single source of the fallback rule — getDomainRank + any ladder display
// (e.g. the Status page's Domain Titles) read through this.
export function getDomainRankLadder(domainId?: string) {
  return (domainId && DOMAIN_RANKS_BY_DOMAIN[domainId]) || DOMAIN_RANKS;
}

// Get domain rank based on total level. With a domainId, uses that domain's
// own ladder when one exists; the generic DOMAIN_RANKS is the fallback.
export function getDomainRank(totalLevel: number, domainId?: string) {
  const ladder = getDomainRankLadder(domainId);
  return ladder.find(rank => totalLevel >= rank.minLevel) || ladder[ladder.length - 1];
}

// Calculate tier information from total level
export function calculateTierInfo(totalLevel: number) {
  const tierIndex = Math.min(Math.floor(totalLevel / XP_CURVE.levelsPerTier), TIERS.length - 1);
  const tier = TIERS[tierIndex];
  const tierLevel = totalLevel % XP_CURVE.levelsPerTier;

  return {
    tier: tier.name,
    tierLevel: tierLevel === 0 && totalLevel > 0 ? XP_CURVE.levelsPerTier : tierLevel,
    totalLevel,
    tierColor: tier.color
  };
}

// Get tier name from total level
export function getTierName(totalLevel: number): string {
  const tierIndex = Math.min(Math.floor(totalLevel / XP_CURVE.levelsPerTier), TIERS.length - 1);
  return TIERS[tierIndex].name;
}

// Get tier color from total level
export function getTierColor(totalLevel: number): string {
  const tierIndex = Math.min(Math.floor(totalLevel / XP_CURVE.levelsPerTier), TIERS.length - 1);
  return TIERS[tierIndex].color;
}

// Calculate points based on user level
export function calculateLevelBasedPoints(basePoints: number, userLevel: number): number {
  // Ensure userLevel is a valid number
  userLevel = Math.max(1, isNaN(userLevel) ? 1 : userLevel);

  // Start scaling from level 1
  const scalingFactor = Math.pow(XP_CURVE.pointsScaling, userLevel - 1);
  return Math.round(basePoints * scalingFactor);
}

// Look up the streak multiplier from the (config-hydrated) ladder.
function streakLadderMultiplier(streak: number): number {
  let multiplier = 1.0;
  for (const step of STREAK_LADDER) {
    if (streak >= step.threshold) multiplier = step.multiplier;
    else break;
  }
  return multiplier;
}

// Calculate streak multiplier and XP for habits
export function getStreakMultiplier(streak: number) {
  const multiplier = streakLadderMultiplier(streak);
  return { multiplier, xp: Math.round(POINTS.HABIT * multiplier) };
}

// Get breathing streak multiplier from breathwork streak
export function getBreathingStreakMultiplier(breathworkStreak: number): number {
  return streakLadderMultiplier(breathworkStreak);
}

// Calculate domain streak bonus based on active habits
export function calculateDomainStreakBonus(habits: Array<{ streak: number }>) {
  if (habits.length === 0) return 1.0;

  // Get the highest streak multiplier from all habits
  const maxMultiplier = Math.max(
    ...habits.map(habit => getStreakMultiplier(habit.streak).multiplier)
  );

  // Apply a diminishing return to the bonus
  return 1.0 + (maxMultiplier - 1.0) * XP_CURVE.domainStreakDamping;
}

// Calculate level info for domains using geometric progression with infinite tier progression
export function calculateDomainLevelInfo(points: number = 0) {
  points = isNaN(points) ? 0 : Math.max(0, points);

  let totalLevel = 0;
  let accumulatedPoints = 0;

  // Calculate total level by accumulating points infinitely
  while (true) {
    const currentTier = Math.floor(totalLevel / XP_CURVE.levelsPerTier);
    const levelInTier = totalLevel % XP_CURVE.levelsPerTier;

    // Calculate points needed for next level within this tier
    const pointsNeededForNextLevel = Math.floor(XP_CURVE.domainBasePoints * Math.pow(XP_CURVE.levelMultiplier, levelInTier));

    if (accumulatedPoints + pointsNeededForNextLevel > points) {
      break;
    }

    accumulatedPoints += pointsNeededForNextLevel;
    totalLevel++;
  }

  // Calculate tier information
  const tierInfo = calculateTierInfo(totalLevel);

  // Calculate points needed for next level
  const levelInTier = totalLevel % XP_CURVE.levelsPerTier;
  const pointsForNextLevel = Math.floor(XP_CURVE.domainBasePoints * Math.pow(XP_CURVE.levelMultiplier, levelInTier));

  // Calculate remaining points in current level
  const remainingPoints = points - accumulatedPoints;

  // Calculate progress percentage for current level
  const progress = Math.floor((remainingPoints / pointsForNextLevel) * 100);

  return {
    level: tierInfo.tierLevel,
    totalLevel: totalLevel,
    tier: tierInfo.tier,
    tierColor: tierInfo.tierColor,
    currentPoints: remainingPoints,
    pointsForNextLevel: pointsForNextLevel,
    progress: isNaN(progress) ? 0 : Math.min(progress, 100),
    pointsToNextLevel: Math.max(0, pointsForNextLevel - remainingPoints)
  };
}

// Calculate level info for overall user level using geometric progression with infinite tier progression
export function calculateLevelInfo(points: number = 0) {
  points = isNaN(points) ? 0 : Math.max(0, points);

  let totalLevel = 0;
  let accumulatedPoints = 0;

  // Calculate total level by accumulating points infinitely
  while (true) {
    const currentTier = Math.floor(totalLevel / XP_CURVE.levelsPerTier);
    const levelInTier = totalLevel % XP_CURVE.levelsPerTier;

    // Calculate points needed for next level within this tier
    const pointsNeededForNextLevel = Math.floor(XP_CURVE.userBasePoints * Math.pow(XP_CURVE.levelMultiplier, levelInTier));

    if (accumulatedPoints + pointsNeededForNextLevel > points) {
      break;
    }

    accumulatedPoints += pointsNeededForNextLevel;
    totalLevel++;
  }

  // Calculate tier information
  const tierInfo = calculateTierInfo(totalLevel);

  // Calculate points needed for next level
  const levelInTier = totalLevel % XP_CURVE.levelsPerTier;
  const pointsForNextLevel = Math.floor(XP_CURVE.userBasePoints * Math.pow(XP_CURVE.levelMultiplier, levelInTier));

  // Calculate remaining points in current level
  const remainingPoints = points - accumulatedPoints;

  // Calculate progress percentage for current level
  const progress = Math.floor((remainingPoints / pointsForNextLevel) * 100);

  return {
    level: tierInfo.tierLevel,
    totalLevel: totalLevel,
    tier: tierInfo.tier,
    tierColor: tierInfo.tierColor,
    currentPoints: remainingPoints,
    pointsForNextLevel: pointsForNextLevel,
    progress: isNaN(progress) ? 0 : Math.min(progress, 100),
    pointsToNextLevel: Math.max(0, pointsForNextLevel - remainingPoints)
  };
}

// Format large numbers with K/M suffix
export function formatPoints(points: number = 0): string {
  // Handle NaN and undefined
  if (!points && points !== 0) return '0';

  // Ensure points is a number
  points = isNaN(points) ? 0 : points;

  if (points >= 1000000) {
    return `${(points / 1000000).toFixed(1)}M`;
  }
  if (points >= 1000) {
    return `${(points / 1000).toFixed(1)}K`;
  }
  return points.toString();
}

// Calculate ability level cap based on domain level
// Domain level 0-9 = cap 10, 10-19 = cap 20, 20-29 = cap 30, etc.
export function calculateAbilityLevelCap(domainLevel: number): number {
  const domainTier = Math.floor(domainLevel / 10);
  return (domainTier + 1) * 10;
}

// Check if an ability has reached its domain-based level cap
export function isAbilityAtCap(abilityLevel: number, domainLevel: number): boolean {
  const cap = calculateAbilityLevelCap(domainLevel);
  return abilityLevel >= cap;
}

// Calculate how much XP can be applied to ability based on domain level cap
export function calculateMaxApplicableXP(domainLevel: number): number {
  const levelCap = calculateAbilityLevelCap(domainLevel);

  // XP thresholds for levels 1-10 (exponential growth)
  const baseXpThresholds = [
    0,      // Level 1 (start)
    150,    // Level 2
    350,    // Level 3
    600,    // Level 4
    950,    // Level 5
    1400,   // Level 6
    2000,   // Level 7
    2750,   // Level 8
    3700,   // Level 9
    5000    // Level 10
  ];

  // Calculate XP needed to reach the cap
  // For caps beyond level 10, we need to extrapolate
  if (levelCap <= 10) {
    return baseXpThresholds[levelCap - 1];
  }

  // For higher caps, continue the exponential pattern
  let totalXP = baseXpThresholds[9]; // XP to reach level 10
  const growthRate = 1.4; // Approximate growth rate between levels

  for (let level = 10; level < levelCap; level++) {
    const xpForNextLevel = Math.floor(baseXpThresholds[9] * Math.pow(growthRate, level - 9));
    totalXP += xpForNextLevel;
  }

  return totalXP;
}

// Split XP between applied (counts toward ability level) and excess (stored for later)
export function splitAbilityXP(totalXP: number, domainLevel: number): { appliedXP: number; excessXP: number } {
  const maxApplicable = calculateMaxApplicableXP(domainLevel);

  if (totalXP <= maxApplicable) {
    return { appliedXP: totalXP, excessXP: 0 };
  }

  return {
    appliedXP: maxApplicable,
    excessXP: totalXP - maxApplicable
  };
}